import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import AcademicsEditModal from './components/AcademicsEditModal';
import { SUPPORTED_FILE_ACCEPT, appendPreservedAcademics, validateFile } from './academicsFileUpload';
import Img from '../../../Img';
import { LinkAnchor } from '../../../Links';
import ProspectSectionRow from '../components/ProspectSectionRow';
import { getPlayerStudentInfo, updatePlayerStudentInfo } from '../../../../actions/profile-actions';
import useCPPTracking from '../../../hooks/useCPPTracking';
import { CppPlayerActions } from '../../../Analytics/constants';
import config from '../../../../../config';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.academics';

const DOCUMENTS = {
    transcript: {
        testKey: 'transcript',
        label: 'Transcript',
        field: 'transcript',
        updateFlag: 'updateTranscript',
        downloadPath: 'transcript',
        fileNameKey: 'transcriptFileName',
    },
    tennisResume: {
        testKey: 'tennis-resume',
        label: 'Tennis Resume',
        field: 'tennisResume',
        updateFlag: 'updateTennisResume',
        downloadPath: 'tennisresume',
        fileNameKey: 'tennisResumeFileName',
    },
};

const renderRow = (label, value, testId) => (
    <ProspectSectionRow
        label={label}
        value={value}
        testId={testId}
        testIdPrefix={TESTID_PREFIX}
    />
);

const Academics = () => {
    const dispatch = useDispatch();
    const { trackPlayerEngagement } = useCPPTracking();
    const profileData = useSelector((state) => state.profile?.data) || {};
    const authUser = useSelector((state) => state.auth?.user);
    const studentInfo = profileData.studentInfo || {};
    const playerId = profileData.id;
    const isOwnProfile = !!authUser && !!profileData.memberId && authUser.id === profileData.memberId;

    const [isEditing, setIsEditing] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [uploadError, setUploadError] = useState(null);

    const graduatingDisplay = studentInfo.graduationDate
        ? moment.utc(studentInfo.graduationDate).format('MMMM YYYY')
        : '';

    const highSchoolValue = (
        <span data-testid={`${TESTID_PREFIX}.high-school.text`}>
            <div>{studentInfo.highSchoolName || ''}</div>
            <div>{studentInfo.highSchoolLocation || ''}</div>
        </span>
    );

    const handleDocumentUpload = async (event, docKey) => {
        const file = event.target.files?.[0] || null;
        event.target.value = '';
        if (!file) {
            return;
        }
        const doc = DOCUMENTS[docKey];
        const validationError = validateFile(file, doc.label);
        if (validationError) {
            setUploadError({ docKey, message: validationError });
            return;
        }
        setUploadError(null);
        setUploadingDoc(docKey);
        try {
            const formData = new FormData();
            formData.append('Context', 'onboardingAcademics');
            appendPreservedAcademics(formData, studentInfo);
            formData.append(doc.updateFlag, 'true');
            formData.append(doc.field, file);
            const ok = await Promise.resolve(dispatch(updatePlayerStudentInfo(formData)));
            if (!ok) {
                throw new Error('upload failed');
            }
            if (playerId) {
                await Promise.resolve(dispatch(getPlayerStudentInfo(playerId)));
            }
            trackPlayerEngagement(CppPlayerActions.CPP_ACADEMICS_UPDATED);
        } catch (err) {
            const serverMessage = err?.response?.data?.DisplayMessage;
            setUploadError({
                docKey,
                message: serverMessage || `We couldn't upload your ${doc.label.toLowerCase()}. Please try again.`,
            });
        } finally {
            setUploadingDoc(null);
        }
    };

    const renderDocumentValue = (docKey) => {
        const doc = DOCUMENTS[docKey];
        const fileName = studentInfo[doc.fileNameKey];
        const isUploading = uploadingDoc === docKey;
        const error = uploadError && uploadError.docKey === docKey ? uploadError.message : null;
        return (
            <span className={styles.academicsDocCell} data-testid={`${TESTID_PREFIX}.${doc.testKey}.cell`}>
                {fileName && (
                    <LinkAnchor
                        link={`${config.apiHost}/v1/player/${playerId}/studentinfo/${doc.downloadPath}`}
                        useAnchor
                        testId={`${TESTID_PREFIX}.${doc.testKey}.link`}
                    >
                        View
                    </LinkAnchor>
                )}
                {isUploading && (
                    <span className={styles.academicsDocStatus} data-testid={`${TESTID_PREFIX}.${doc.testKey}.uploading`}>
                        Uploading…
                    </span>
                )}
                {isOwnProfile && !isUploading && (
                    <label
                        className={styles.academicsUploadLink}
                        data-testid={`${TESTID_PREFIX}.${doc.testKey}.upload-label`}
                    >
                        <span data-testid={`${TESTID_PREFIX}.${doc.testKey}.upload-text`}>
                            {fileName ? 'Replace' : 'Upload'}
                        </span>
                        <input
                            type="file"
                            accept={SUPPORTED_FILE_ACCEPT}
                            onChange={(e) => handleDocumentUpload(e, docKey)}
                            className={styles.academicsHiddenFileInput}
                            data-testid={`${TESTID_PREFIX}.${doc.testKey}.file-input`}
                        />
                    </label>
                )}
                {error && (
                    <span className={styles.academicsDocError} data-testid={`${TESTID_PREFIX}.${doc.testKey}.error`}>
                        {error}
                    </span>
                )}
            </span>
        );
    };

    return (
        <div className={styles.academicsSection} data-testid={`${TESTID_PREFIX}.container`}>
            <div className={styles.academicsHeader}>
                <div className={styles.academicsTitleBlock}>
                    <div
                        className={styles.academicsTitle}
                        data-testid={`${TESTID_PREFIX}.title`}
                    >
                        Academics
                    </div>
                    <div
                        className={styles.academicsSubtitle}
                        data-testid={`${TESTID_PREFIX}.subtitle`}
                    >
                        Viewable by college coaches.
                    </div>
                </div>
                {isOwnProfile && (
                    <button
                        type="button"
                        className={styles.academicsEditBtn}
                        onClick={() => setIsEditing(true)}
                        data-testid={`${TESTID_PREFIX}.edit-btn`}
                    >
                        <Img
                            src="/img/v4/pen.svg"
                            alt=""
                            className={styles.academicsEditIcon}
                        />
                        <span data-testid={`${TESTID_PREFIX}.edit-btn.text`}>Edit</span>
                    </button>
                )}
            </div>

            <div className={styles.academicsCard}>
                <div className={styles.academicsBody}>
                    {renderRow('High School', highSchoolValue, 'high-school')}
                    {renderRow('Graduating', graduatingDisplay, 'graduating')}
                    {renderRow('High School GPA', studentInfo.highSchoolGpa, 'high-school-gpa')}
                    {renderRow('SAT Score', studentInfo.scores?.sat, 'sat-score')}
                    {renderRow('ACT Score', studentInfo.scores?.act, 'act-score')}
                    {renderRow('TOEFL Score', studentInfo.scores?.toefl, 'toefl-score')}
                    {renderRow('Current Coursework', studentInfo.coursework, 'current-coursework')}
                    {renderRow('Major of Interest', studentInfo.majorOfInterest, 'major-of-interest')}
                    {renderRow('Transcript', renderDocumentValue('transcript'), 'transcript')}
                    {renderRow('Tennis Resume', renderDocumentValue('tennisResume'), 'tennis-resume')}
                </div>
            </div>

            {isEditing && isOwnProfile && (
                <AcademicsEditModal onClose={() => setIsEditing(false)} />
            )}
        </div>
    );
};

export default Academics;
